<!-- Component for the main Expansion Tab "Dashboard" -->
<template>
  <div class="pt-4">
    <Pencil class="w-0 h-0 pointer-events-none" />
    <Trash2 class="w-0 h-0 pointer-events-none" />
    <div v-if="journeys.length == 0" class="bg-white dark:bg-dark-box-background flex justify-center min-h-[200px] rounded-lg m-2">
      <div class="flex justify-center items-center flex-col">
        <h3 class="mt-2 text-xl font-normal text-black dark:text-white">
          {{ $t("You currently don't have any journeys") }}
        </h3>
        <p class="mt-2 text-md font-normal text-gray-500">
          {{ $t("Add a new journey to get started!") }}
        </p>
        <div class="primary-btn w-fit" @click="$emitter.emit(ALL_EMITTERS.ADD_NEW_JOURNEY)">
          <Plus class="w-5 h-5 ml-1" />
          <div class="pl-1">
            <span>{{ $t("Create your first journey") }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="flex w-full">
        <div class="w-full ml-4 relative">
          <Spinner v-if="phases_loading" class="absolute z-20 right-3 mt-2" />
          <TextInput
            v-model="search_record_term"
            :validate-on-input="true"
            :label="$t('Search for records')"
            :classes="'w-full'"
            input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
            name="name"
            type="text"
            :placeholder="$t('Search for records')" />
        </div>
        <div class="w-3/4 ml-4">
          <div class="relative z-30 border-red-300 rounded-md -mt-1">
            <SelectGroupDropDownMenu
              v-model:current-item="selected_group"
              :adjust-height="true"
              :items="
                $store.state.data?.groups.filter(
                  (group) => group.type == current_journey?.type && group.organization == $store.state.organization.id
                )
              "
              :object-type="current_journey?.type"
              :clear-selected-item="!selected_group ? true : false"
              label-attr="name"
              :placeholder="$t('Filter by group')"
              :is-box-background="true"
              input-classes="sm:text-sm appearance-none h-10 font-medium text-gray-700 text-left block w-full px-3 py-2 w-48 rounded-lg shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light " />
          </div>
        </div>
        <div class="ml-2.5 w-full -mt-2">
          <TeamMembersSelect
            v-model:selected-team-members="team_members_selected"
            display-style="inline"
            :label="$t('Filter by team members')" />
        </div>

        <div class="ml-auto mr-6">
          <SelectDropDownMenu
            v-model:current-item="selected_view"
            :show-label="true"
            label-attr="label"
            value-attr="value"
            :allow-null="false"
            :items="selected_view_options"
            :label="$t('View by')"
            :placeholder="$t('View by')"
            class="w-[200px]"
            input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light " />
        </div>
      </div>
    </div>

    <TripleMenu
      :set-edit-mode="setEditMode"
      :edit-mode-active="edit_mode_active"
      :current-template="currentTemplate"
      :quick-actions="journeys_top_right_menu_actions"
      :components-actions="top_right_menu_components_actions"
      :page-reference="PAGE_REFERENCES.CUSTOMER_JOURNEYS" />

    <div class="w-full h-full overflow-auto">
      <transition duration="300" mode="out-in" name="fade">
        <JourneysMain
          v-if="selected_view == 'journey'"
          :current-journey="current_journey"
          :journeys="journeys"
          :object-type="current_journey?.type" />
        <DetailedJourneysView v-else :current-journey="current_journey" :journeys="journeys" />
      </transition>
    </div>
  </div>
</template>

<script>
import { markRaw } from "vue"
import Spinner from "@/components/shared/LoadingSpinner.vue"
import ModuleIcon from "@/components/shared/icons/ModuleIcon.vue"
import TeamMembersSelect from "@/components/shared/TeamMembersSelect.vue"
import JourneysMain from "@/views/dashboard/expansion/journeys/JourneysMain/index.vue"
import dataService from "@/services/data.service"
import { PAGE_REFERENCES } from "@/store/constants/references"
import TripleMenu from "@/components/shared/TripleMenu.vue"
import { Plus, Trash2, Pencil, Copy } from "lucide-vue-next"
import DetailedJourneysView from "@/views/dashboard/expansion/journeys/DetailedJourneysView/index.vue"

import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import SelectDropDownMenu from "@/components/shared/SelectDropDownMenu.vue"
import SelectGroupDropDownMenu from "@/components/shared/SelectGroupDropDownMenu.vue"
import TextInput from "@/components/shared/TextInput.vue"

import { VIEW_TYPES } from "@/store/constants/references"

export default {
  components: {
    SelectDropDownMenu,
    Spinner,
    TextInput,
    SelectGroupDropDownMenu,
    DetailedJourneysView,
    JourneysMain,
    TeamMembersSelect,
    Plus,
    TripleMenu,
    Pencil: markRaw(Pencil),
    Trash2: markRaw(Trash2)
  },
  props: {
    currentTemplate: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      ALL_EMITTERS,
      PAGE_REFERENCES,
      search_record_term: "",
      selected_group: null,
      current_journey: null,
      journeys: [],
      team_members_selected: [],
      selected_view: "",
      phases_loading: false,
      edit_mode_active: false,
      selected_view_options: [
        { label: this.$t("Journey"), value: "journey" },
        { label: this.$t("Detailed"), value: "detailed" }
      ]
    }
  },

  computed: {
    journeys_top_right_menu_actions() {
      return [
        {
          name: this.$t("Add") + ` ${this.$utils.getObjectTypeByType(this.current_journey?.type)?.label}`,
          action: () => {
            const metrics_obj = {
              object_type: this.current_journey?.type,
              page_reference: PAGE_REFERENCES.CUSTOMER_JOURNEYS,
              view_type: VIEW_TYPES.FORM_CREATE
            }
            this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
          },
          icon: Plus
        },

        {
          name: this.$t("Create New Phase"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_NEW_PHASE, {
              journeys: this.journeys,
              selected_journey: this.current_journey
            })
          },
          disabled: false,
          icon: Plus
        },
        {
          name: this.$t("Edit Journey"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.EDIT_JOURNEY, {
              journey: this.current_journey
            })
          },
          disabled: false,
          icon: Pencil
        },
        {
          name: this.$t("Duplicate Journey"),
          action: () => {
            this.duplicateJourney(this.current_journey)
          },
          disabled: false,
          icon: Copy
        },
        {
          name: this.$t("Delete Journey"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.DELETE_JOURNEY, {
              journey: this.current_journey
            })
          },
          disabled: false,
          icon: Trash2
        }
      ]
    },
    top_right_menu_components_actions() {
      return [
        {
          name: this.$t("Edit Layout"),
          action: () => {
            this.edit_mode_active = true
            this.$emitter.emit(ALL_EMITTERS.EDIT_LAYOUT, {
              page_reference: PAGE_REFERENCES.CUSTOMER_JOURNEYS,
              page_name: this.currentTemplate.page
            })
          },
          disabled: false,
          keyBoardShortcut: "I",
          icon: ModuleIcon
        },
        {
          name: this.$t("Add/Remove Components"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.ADD_NEW_COMPONENT, {
              page_reference: PAGE_REFERENCES.CUSTOMER_JOURNEYS,
              current_template: this.currentTemplate
            })
          },
          disabled: false,
          keyBoardShortcut: "U",
          icon: ModuleIcon
        },
        {
          name: this.currentTemplate?.is_static ? this.$t("Reset Page") : this.$t("Delete Page"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.DELETE_PAGE, this.currentTemplate)
          },
          icon: ModuleIcon
        }
      ]
    }
  },
  watch: {
    search_record_term() {
      this.$utils.debounce(() => {
        this.getFilteredJourneys(true)
      }, 800)
    },
    selected_view() {
      this.$store.dispatch("ui/setUIValue", {
        selected_journeys_view: this.selected_view
      })
    },
    selected_group() {
      this.getFilteredJourneys()
    },
    team_members_selected: {
      deep: true,
      handler() {
        this.$emitter.emit(
          ALL_EMITTERS.SELECTED_USERS,
          this.team_members_selected.map((x) => x.id)
        )
        this.getFilteredJourneys()
      }
    }
  },
  activated() {
    this.register_listeners()
    this.init()
    if (this.journeys.length) {
      this.$router.push(
        `/expansion/journeys?journey=${this.$route.query?.journey ? this.$route.query.journey : this.journeys[0].id}&object=${
          this.$route.query?.object ? this.$route.query?.object : this.journeys[0].type
        }`
      )
    }
  },

  created() {
    if (this.$store.state.ui.selected_journeys_view) {
      this.selected_view = this.$store.state.ui.selected_journeys_view
    } else {
      this.selected_view = "journey"
    }

    this.init()
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.IS_EDIT_MODE_ACIVE, (edit_mode) => {
        this.edit_mode_active = edit_mode
      })
      this.$emitter.on(ALL_EMITTERS.INIT_PHASES_IN_JOURNEYS, () => {
        this.init(true)
      })
      this.$emitter.on(ALL_EMITTERS.CURRENT_JOURNEY_CHANGED, (journey) => {
        this.$router.push(`/expansion/journeys?journey=${journey.id}&object=${journey.type}`)
      }),
        this.$emitter.on(ALL_EMITTERS.UPDATE_JOURNEY_IN_JOURNEYS, (journey) => {
          this.updateJourney(journey)
        })

      this.$emitter.on(ALL_EMITTERS.JOURNEY_DELETED_AND_DISTRIBUTION_DONE_IN_JOURNEYS, (journey) => {
        if (journey.id == this.current_journey?.id || !this.current_journey?.id) {
          this.current_journey = null
          this.$router.push(`/expansion/journeys`)
        }
      })

      //Add new phase
      this.$emitter.on(ALL_EMITTERS.PHASES_ADDED_TO_JOURNEYS, (phase) => {
        this.$store.dispatch("ui/showLoading")
        dataService.getPhase(phase.id).then((res) => {
          res.data.funnels.forEach((journey_id) => {
            let journey = this.journeys.find((fun) => fun.id == journey_id)
            if ((journey?.phases.length > 0 && typeof journey?.phases[0] != "string") || journey?.phases.length == 0) {
              journey.phases.push(res.data)
              this.$utils.replaceItem(this.journeys, journey)
              if (journey_id == this.current_journey.id) {
                //this.current_journey = this.$utils.clone(journey)
              }
            }
          })
          this.$store.dispatch("ui/hideLoading")
          /* this.loadPhaseCompanies(res.data).then((ph) => {
          this.updatePhaseInJourneys(ph)
        }) */
        })
      })

      //Add exisiting phase
      this.$emitter.on(ALL_EMITTERS.PHASE_MODIFIED_IN_JOURNEYS, () => {
        this.init(true)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.IS_EDIT_MODE_ACIVE)
      this.$emitter.off(ALL_EMITTERS.INIT_PHASES_IN_JOURNEYS)
      this.$emitter.off(ALL_EMITTERS.CURRENT_JOURNEY_CHANGED)
      this.$emitter.off(ALL_EMITTERS.UPDATE_JOURNEY_IN_JOURNEYS)
      this.$emitter.off(ALL_EMITTERS.JOURNEY_DELETED_AND_DISTRIBUTION_DONE_IN_JOURNEYS)
      this.$emitter.off(ALL_EMITTERS.PHASES_ADDED_TO_JOURNEYS)
      this.$emitter.off(ALL_EMITTERS.PHASE_MODIFIED_IN_JOURNEYS)
    },
    // updatePhaseInJourneys(phase) {
    //   phase.journeys.forEach((journey_id) => {
    //     let journey = this.journeys.find((fun) => fun.id == journey_id)
    //     if ((journey?.phases.length > 0 && typeof journey?.phases[0] != "string") || journey.phases.length == 0) {
    //       journey.phases = this.$utils.replaceItem(journey.phases, phase)
    //       this.$utils.replaceItem(this.journeys, journey)
    //       if (journey_id == this.current_journey.id) {
    //         this.current_journey = this.$utils.clone(journey)
    //       }
    //     }
    //   })
    // },
    getJourneyData(journey, update) {
      this.current_journey = this.$utils.clone(this.journeys.find((x) => x.id == (journey.id ? journey.id : journey)))
      this.$gtag.event("customer_journey_click", {
        event_label: "Customer journey",
        name: this.current_journey?.name,
        phases_count: this.current_journey?.length,
        object_type: this.current_journey?.type
      })
      if (!update) {
        this.$store.dispatch("ui/showLoading")
      }

      const params = {}
      if (this.team_members_selected.length) {
        params["co_owners"] = this.team_members_selected.map((x) => x.id).join(",")
      }
      if (this.selected_group) {
        params["filter"] = this.selected_group.filter
      }
      if (this.search_record_term) {
        params["name"] = this.search_record_term
      }

      dataService.getJourney(journey?.id ? journey.id : journey, params).then((res) => {
        this.current_journey = res.data
        this.$utils.replaceItem(this.journeys, this.current_journey)
        this.$store.dispatch("ui/hideLoading")
      })
    },

    updateJourney(journey) {
      this.$store.dispatch("ui/showLoading")
      this.current_journey = journey
      this.$utils.replaceItem(this.journeys, this.current_journey)
      this.$store.dispatch("ui/hideLoading")
    },

    duplicateJourney(journey) {
      dataService.duplicateJourney(journey.id).then((res) => {
        this.$emitter.emit(ALL_EMITTERS.JOURNEY_ADDED, res.data)
      })
    },

    getFilteredJourneys(dont_show_loading) {
      if (!dont_show_loading) {
        this.$store.dispatch("ui/showLoading")
      } else {
        this.phases_loading = true
      }
      const params = {}
      if (this.team_members_selected.length) {
        params["co_owners"] = this.team_members_selected.map((x) => x.id).join(",")
      }
      if (this.selected_group) {
        params["filter"] = this.selected_group.filter
      }
      if (this.search_record_term) {
        params["name"] = this.search_record_term
      }
      dataService.getJourney(this.current_journey.id, params).then((res) => {
        this.current_journey = res.data
        this.$utils.replaceItem(this.journeys, this.current_journey)
        this.$store.dispatch("ui/hideLoading")
        this.phases_loading = false
      })
    },

    init(update) {
      if (!update) {
        this.$store.dispatch("ui/showLoading")
      }

      dataService.getJourneys({ page_size: 10 }).then((result) => {
        if (!update) {
          this.journeys = result.data.results
        }
        if (this.$route.query?.journey) {
          this.getJourneyData(this.$route.query?.journey, true)
        } else if (result.data.results.length > 0) {
          this.$router.push(
            `/expansion/journeys?journey=${this.$route.query?.journey ? this.$route.query.journey : this.journeys[0].id}&object=${
              this.$route.query?.object ? this.$route.query?.object : this.journeys[0].type
            }`
          )
        }
        this.$store.dispatch("ui/hideLoading")
        if (update) {
          this.$emitter.emit(ALL_EMITTERS.METRICS_UPDATED_IN_OBJECT_VIEW)
        }
      })
    },

    setEditMode(value) {
      this.edit_mode_active = value
      this.$emitter.emit(ALL_EMITTERS.EDIT_LAYOUT, {
        page_reference: PAGE_REFERENCES.CUSTOMER_JOURNEYS,
        page_name: this.currentTemplate.page
      })
    }
    // loadPhaseCompanies(phase) {
    //   if (phase?.records?.next) {
    //     return dataService.getRecordsByUrl(phase.records.next, { filter: phase.filter, filter_metrics: phase.filter_metrics }).then((result) => {
    //       phase.records.next = result.data.next
    //       phase.records.results.push(...result.data.results)
    //       return phase
    //     })
    //   }
    // }
  }
}
</script>
